"use client";

import { FlexRowAlignCenter } from "@/components/Flex";
import LazyLoaded from "@/components/LazyLoaded";
import WithOptionsMenu, { WithMenuOption } from "@/components/WithOptionsMenu";
import FlashcardsToPrint from "@/components/toPrint/FlashcardsToPrint";
import { FlashcardsToPrintHandle } from "@/components/toPrint/FlashcardsToPrint/FlashcardsToPrint";
import { AuthEvent } from "@/features/Auth/AuthPopup/utils/constants";
import { useMoveToPopupContextSelector } from "@/features/MoveToPopup/hooks/useMoveToPopupContext";
import { useContentItemsSharingPopupContextSelector } from "@/features/ShareSettings/hooks/useContentItemsSharingContext";
import { useTaggingContextSelector } from "@/features/Tagging/hooks/useTaggingContext";
import { useBreakPoints } from "@/hooks/styles/useBreakpoints";
import { useCheckAuth } from "@/hooks/useCheckAuth";
import useConfirmDialog from "@/hooks/useConfirmDialog";
import { iconSizes, strokeWidth } from "@/utils/iconProps";
import { isOnHome } from "@/utils/pathUtils";
import { themeColors } from "@/utils/themeColors";
import { ClassPermission, FlashcardSet, ItemType } from "@knowt/syncing/graphql/schema";
import { useBookmark } from "@knowt/syncing/hooks/bookmarks/useBookmarks";
import { useClass } from "@knowt/syncing/hooks/classes/useClass";
import { useFlashcardSet } from "@knowt/syncing/hooks/flashcards/useFlashcards";
import {
    deleteFlashcardSets,
    duplicateFlashcardSet,
    moveFlashcardSets,
    restoreFlashcardSets,
    trashFlashcardSets,
} from "@knowt/syncing/hooks/flashcards/utils";
import { useUpdateNote } from "@knowt/syncing/hooks/notes/useNotes";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { useRecentlyViewed } from "@knowt/syncing/hooks/views/useRecentlyViewed";
import { createQueryStrings, renameKey, retry } from "@knowt/syncing/utils/genericUtils";
import noop from "@knowt/syncing/utils/noop";
import { Portal } from "@mui/base/Portal";
import { SxProps } from "@mui/material/styles";
import {
    Bookmark,
    BookmarkMinus,
    Copy,
    Download,
    ExternalLink,
    FolderInput,
    Globe,
    KeyRound,
    Lock,
    Merge,
    Pen,
    Pin,
    PinOff,
    Printer,
    Tag,
    Trash,
    TriangleAlert,
    Undo,
} from "lucide-react";
import dynamic from "next/dynamic";
import { useParams, usePathname } from "next/navigation";
import { useRouter } from "@bprogress/next";
import { memo, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { ThreeDotsBtn } from "./UserContentCardBtns";

const ExportFlashcardsPopup = dynamic(() => import("@/components/Popup/ExportFlashcardsPopup"));

type FlashcardSetOptionsMenuProps = {
    flashcardSet: FlashcardSet;
    btnSx?: SxProps;
    radius?: string;
};

/**
 * Opinionated menu for flashcard card
 * NOTE: this is very similar to `./NoteThreeDotsMenuHandler` but the behavior (the hook inside) is different.
 * so we separate the component for each
 */
const FlashcardSetOptionsMenu = ({ flashcardSet: initialFlashcardSet, btnSx }: FlashcardSetOptionsMenuProps) => {
    const router = useRouter();
    const pathname = usePathname();
    const { folderId } = useParams<{ folderId: string }>();

    const { userId, user, isStudent } = useCurrentUser();
    const { checkAuth } = useCheckAuth();
    const { openConfirmDialog } = useConfirmDialog();
    const { smDown } = useBreakPoints();
    const [shouldHookLoad, setShouldHookLoad] = useState(false);
    const { remove } = useRecentlyViewed();
    const flashCardToPrintRef = useRef<FlashcardsToPrintHandle>(null);

    const { updateFlashcardSetMetaData, flashcardSet: reactiveFlashcardSet } = useFlashcardSet({
        fallbackData: initialFlashcardSet,
        isEnabled: shouldHookLoad,
        flashcardSetId: initialFlashcardSet.flashcardSetId,
    });

    const flashcardSet = reactiveFlashcardSet ?? initialFlashcardSet;
    const { flashcardSetId, noteId } = flashcardSet;
    const { update: updateNote } = useUpdateNote(noteId);

    const readOnly = !userId || !flashcardSet.userId || flashcardSet.userId !== userId;
    const isClassItem = !!flashcardSet?.classId;

    const { permission } = useClass({ classId: flashcardSet?.classId });

    const courseReadOnly = readOnly && permission !== ClassPermission.ALL;

    const draft = flashcardSet.draft;
    const flashcardRoute = draft ? `/flashcards/${flashcardSetId}/edit` : `/flashcards/${flashcardSetId}`;

    const { bookmarked, toggleBookmarked } = useBookmark({
        itemId: flashcardSetId,
        type: ItemType.FLASHCARDSET,
        classId: flashcardSet.classId,
        itemUserId: flashcardSet.userId,
    });

    const openTaggingPopup = useTaggingContextSelector(state => state.openTaggingPopup);
    const closeTaggingPopup = useTaggingContextSelector(state => state.closeTaggingPopup);

    const openContentItemsSharingPopup = useContentItemsSharingPopupContextSelector(
        state => state.openContentItemsSharingPopup
    );

    const [isExportPopupOpen, setIsExportPopupOpen] = useState(false);

    const openMoveToPopup = useMoveToPopupContextSelector(state => state?.openMoveToPopup);

    const trashOrRemoveFromClass = () =>
        openConfirmDialog({
            title: flashcardSet?.classId
                ? "Are you sure you want to remove this flashcard set from class?"
                : "Are you sure you want to trash this flashcard set?",
            subtitle: flashcardSet?.classId
                ? "You can re-add this flashcard set to the class later if you continue."
                : "This flashcard set will be moved to the trash and can be recovered later if needed.",
            style: { borderRadius: "4rem" },
            rounded: true,
            cardSize: smDown ? "31rem" : "58rem",
            spacing: 2.2,
            subtitleFontSize: "1.9rem",
            subtitleFontWeight: "500",
            buttonColor: "black",
            buttonTextColor: "white",
            simpleCancelBtn: true,
            onConfirm: async () => {
                if (flashcardSet?.classId) {
                    toast.loading("Removing flashcard set from class...", { id: "DELETE_FLASHCARD_SET" });
                    await moveFlashcardSets({
                        flashcardSetIds: [{ userId: flashcardSet.userId, flashcardSetId }],
                        sourceFolderId: flashcardSet.folderId ?? null,
                        destinationFolderId: null,
                        sourceClassId: flashcardSet.classId,
                        destinationClassId: null,
                        sharedSections: null,
                    });
                    toast.success("Flashcard set removed from class", { id: "DELETE_FLASHCARD_SET" });
                    if (pathname.includes("flashcards")) router.push("/materials");
                } else {
                    await trashFlashcardSets({
                        flashcardSetIds: [{ userId: flashcardSet.userId, flashcardSetId }],
                        sourceFolderId: flashcardSet.folderId ?? null,
                    });
                    await remove([flashcardSetId]);
                    toast.success("Flashcard set moved to trash");
                    if (pathname.includes("flashcards")) router.push("/trash");
                }
            },
            open: false,
        });

    const deletePermanently = () => {
        openConfirmDialog({
            title: "Are you sure you want to delete?",
            subtitle: "This flashcard set will not be able to be recovered if you continue to delete it.",
            style: { borderRadius: "4rem" },
            rounded: true,
            cardSize: smDown ? "31rem" : "58rem",
            spacing: 2.2,
            subtitleFontSize: "1.9rem",
            subtitleFontWeight: "500",
            buttonColor: "black",
            buttonTextColor: "white",
            simpleCancelBtn: true,
            onConfirm: async () => {
                toast.success("Flashcard set deleted permanently");
                await deleteFlashcardSets({ flashcardSetIds: [{ userId: flashcardSet.userId, flashcardSetId }] });
                if (noteId) {
                    await updateNote({ flashcardSetId: null });
                }
                if (pathname.includes("flashcards")) {
                    router.push("/materials");
                }
            },
            open: false,
            onClose: (): void => {
                throw new Error("Function not implemented.");
            },
        });
    };

    const SavedIcon = readOnly ? BookmarkMinus : PinOff;
    const UnsavedIcon = readOnly ? Bookmark : Pin;

    const saveText = bookmarked ? (readOnly ? "Unsave" : "Unpin") : readOnly ? "Save" : "Pin";

    const notAnOwnerAlert: WithMenuOption = {
        label: "Not an owner",
        hide: isStudent || !readOnly,
        disabled: true,
        onClick: () => noop(),
        node: (
            <FlexRowAlignCenter
                className="secondaryTextBold1"
                style={{
                    gap: "0.9rem",
                    backgroundColor: themeColors.errorSecondary,
                    padding: "1rem",
                    borderRadius: "1.2rem",
                }}>
                <TriangleAlert size={iconSizes.MD} fill={themeColors.errorPrimary} color={themeColors.errorSecondary} />{" "}
                Not an owner
            </FlexRowAlignCenter>
        ),
        menuItemProps: {
            sx: {
                padding: "0.6rem",
                cursor: "default",
                "&:hover": {
                    backgroundColor: "unset",
                },
                "&:active": {
                    backgroundColor: "unset",
                },
            },
        },
    };

    const saveOption: WithMenuOption = {
        label: saveText,
        onClick: async () => {
            checkAuth({
                event: AuthEvent.BOOKMARK_ITEM,
                onAuthSuccess: async () => {
                    await toggleBookmarked();
                },
            });
        },
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                {bookmarked ? (
                    <SavedIcon strokeWidth={strokeWidth.normal} size={iconSizes.SM} />
                ) : (
                    <UnsavedIcon strokeWidth={strokeWidth.normal} size={iconSizes.SM} />
                )}
                {saveText}
            </FlexRowAlignCenter>
        ),
    };

    const printFlashcardSet = () => {
        flashCardToPrintRef.current?.print();
    };

    const retryPrintingForSomeTime = async () => {
        const toastId = toast.loading("Loading flashcards...");

        try {
            await retry(async () => printFlashcardSet(), 10, 1_000);
            toast.dismiss(toastId);
        } catch {
            toast.error("Oops, seems like the set is too long. Try printing it from the details page", {
                id: toastId,
            });
        }
    };

    const printOption = {
        label: "Export as PDF",
        onClick: async () => {
            try {
                printFlashcardSet();
            } catch {
                retryPrintingForSomeTime();
            }
        },
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                <Printer strokeWidth={strokeWidth.normal} size={iconSizes.SM} />
                {"Export as PDF"}
            </FlexRowAlignCenter>
        ),
    };

    const SharingIcon = flashcardSet?.public ? Globe : flashcardSet?.password ? KeyRound : Lock;

    const manageSharingOption: WithMenuOption = {
        label: "Manage sharing",
        onClick: () => {
            openContentItemsSharingPopup({
                flashcardSets: flashcardSet ? [flashcardSet] : undefined,
                itemType: ItemType.FLASHCARDSET,
            });
        },
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                <SharingIcon strokeWidth={strokeWidth.normal} size={iconSizes.SM} /> Manage sharing
            </FlexRowAlignCenter>
        ),
    };

    const moveToOption: WithMenuOption = {
        label: "Move to",
        onClick: async () => {
            openMoveToPopup({
                onMove: async ({ classId: destinationClassId, folderId: destinationFolderId, sharedSections }) => {
                    moveFlashcardSets({
                        flashcardSetIds: [{ userId: flashcardSet.userId, flashcardSetId }],
                        sourceFolderId: folderId || null,
                        destinationFolderId,
                        sourceClassId: flashcardSet.classId || null,
                        destinationClassId,
                        sharedSections,
                        ...(destinationClassId ? { isPublic: false } : {}),
                    });
                },
                selectedFolders: {},
                currentClassIdOfItem: flashcardSet.classId ?? null,
                currentFolderIdOfItem: folderId ?? null,
            });
        },
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                <FolderInput strokeWidth={strokeWidth.normal} size={iconSizes.SM} /> Move to
            </FlexRowAlignCenter>
        ),
        hide: readOnly && courseReadOnly,
    };

    const openInNewTabOption: WithMenuOption = {
        label: "Open in new tab",
        onClick: () => window.open(window.location.origin + flashcardRoute, "_blank"),
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                <ExternalLink strokeWidth={strokeWidth.normal} size={iconSizes.SM} /> Open in new tab
            </FlexRowAlignCenter>
        ),
    };

    const editSetOption: WithMenuOption = {
        label: "Edit this set",
        onClick: () => router.push(`/flashcards/${flashcardSetId}/edit`),
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                <Pen strokeWidth={strokeWidth.normal} size={iconSizes.SM} /> Edit this set
            </FlexRowAlignCenter>
        ),
        hide: readOnly,
    };

    const duplicateOption: WithMenuOption = {
        label: "Duplicate",
        onClick: async () => {
            const isAuthenticated = await checkAuth({
                event: AuthEvent.DUPLICATE_FLASHCARDS,
            });

            if (!isAuthenticated) return;

            if (flashcardSet.password) {
                return toast.error("Sorry, you cannot duplicate password-protected flashcard sets.");
            }

            const newFlashcardSet = await toast.promise(duplicateFlashcardSet(flashcardSetId, user), {
                loading: "Copying to your notebook",
                success: "Flashcard set duplicated successfully!",
                error: "Failed to duplicate the Flashcard set",
            });

            if (!isOnHome(pathname)) {
                router.push(`/flashcards/${newFlashcardSet.flashcardSetId}/edit`);
            }
        },
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                <Copy strokeWidth={strokeWidth.normal} size={iconSizes.SM} /> Duplicate
            </FlexRowAlignCenter>
        ),
    };

    const editTagsOption: WithMenuOption = {
        label: "Edit tags",
        onClick: () =>
            openTaggingPopup({
                flashcardSet: flashcardSet as FlashcardSet,
                onSave: async newTags => {
                    renameKey(newTags, "exam", "exam_v2");
                    renameKey(newTags, "custom", "tags");
                    await updateFlashcardSetMetaData(newTags);

                    closeTaggingPopup();
                },
            }),
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                <Tag strokeWidth={strokeWidth.normal} size={iconSizes.SM} /> Edit tags
            </FlexRowAlignCenter>
        ),
        hide: readOnly,
    };

    const mergeSetOption: WithMenuOption = {
        label: "Combine",
        onClick: () => {
            const route =
                `/flashcards/merge` +
                createQueryStrings({
                    flashcardSetId,
                    folderId: flashcardSet.userId === user?.ID ? flashcardSet.folderId : null,
                    classId: flashcardSet.userId === user?.ID ? flashcardSet.classId : null,
                });

            checkAuth({
                event: AuthEvent.COMBINE_FLASHCARDS,
                onAuthSuccess: () => router.push(route),
            });
        },
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                <Merge strokeWidth={strokeWidth.normal} size={iconSizes.SM} /> Combine
            </FlexRowAlignCenter>
        ),
    };

    const trashOption: WithMenuOption = {
        label: flashcardSet?.classId ? "Remove from class" : "Trash this set",
        onClick: trashOrRemoveFromClass,
        hide: courseReadOnly,
        menuItemProps: {
            sx: {
                borderTop: `2px solid ${themeColors.neutral1}`,
                height: "4.7rem",
                color: themeColors.errorPrimary,
                fontWeight: "600",
                fontSize: "1.5rem",
            },
        },
    };

    const restoreOption: WithMenuOption = {
        label: "Restore",
        onClick: async () => {
            await restoreFlashcardSets({
                flashcardSetIds: [{ userId: flashcardSet.userId, flashcardSetId }],
                sourceFolderId: flashcardSet.folderId ?? null,
            });
            toast.success("Flashcard set restored");
        },
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                <Undo strokeWidth={strokeWidth.normal} size={iconSizes.SM} /> Restore
            </FlexRowAlignCenter>
        ),
        hide: readOnly,
    };

    const deletePermanentOption: WithMenuOption = {
        label: "Delete this set",
        onClick: deletePermanently,
        hide: readOnly,
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                <Trash strokeWidth={strokeWidth.normal} size={iconSizes.SM} /> Delete this set
            </FlexRowAlignCenter>
        ),
    };

    const exportOption: WithMenuOption = {
        label: "Export",
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                <Download strokeWidth={strokeWidth.normal} size={iconSizes.SM} /> Export
            </FlexRowAlignCenter>
        ),
        onClick: () => setIsExportPopupOpen(true),
    };

    const options: WithMenuOption[] = flashcardSet?.trash
        ? [restoreOption, deletePermanentOption]
        : isClassItem
          ? courseReadOnly
              ? [notAnOwnerAlert, duplicateOption, printOption, openInNewTabOption]
              : [
                    notAnOwnerAlert,
                    saveOption,
                    editSetOption,
                    duplicateOption,
                    moveToOption,
                    manageSharingOption,
                    printOption,
                    openInNewTabOption,
                    !readOnly ? editTagsOption : null,
                    !readOnly ? mergeSetOption : null,
                    exportOption,
                    trashOption,
                ]
          : [
                saveOption,
                duplicateOption,
                editSetOption,
                printOption,
                !readOnly ? manageSharingOption : null,
                moveToOption,
                openInNewTabOption,
                editTagsOption,
                mergeSetOption,
                exportOption,
                trashOption,
            ];

    return (
        <>
            {/* THREE DOTS */}
            <WithOptionsMenu
                options={options}
                menuItemProps={{
                    sx: { padding: "1rem 1.6rem" },
                }}
                menuProps={{
                    PaperProps: {
                        sx: {
                            borderRadius: "2rem",
                            width: "20.6rem",
                            fontSize: "1.4rem",
                            margin: "-0.5rem 0 0 -1.25rem",
                        },
                    },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                }}>
                {({ openMenu }) => (
                    <ThreeDotsBtn
                        itemId={flashcardSetId}
                        onPointerEnter={() => setShouldHookLoad(true)}
                        onClick={e => {
                            openMenu(e);
                            setShouldHookLoad(true);
                        }}
                        sx={{ marginTop: "-0.35rem", ...btnSx }}
                    />
                )}
            </WithOptionsMenu>

            <Portal>
                <LazyLoaded load={isExportPopupOpen}>
                    <div onClick={e => e.stopPropagation()}>
                        <ExportFlashcardsPopup
                            isOpen={isExportPopupOpen}
                            onClose={() => setIsExportPopupOpen(false)}
                            flashcardSetId={flashcardSetId}
                        />
                    </div>
                </LazyLoaded>
            </Portal>

            <div style={{ display: "none" }}>
                <FlashcardsToPrint flashcardSet={flashcardSet} ref={flashCardToPrintRef} />
            </div>
        </>
    );
};

export default memo(FlashcardSetOptionsMenu);
